<template>
  <NewBsBox
    :bs="{
      display: 'inline-flex',
      minWidth: 178 * localLevelNums + (localLevelNums - 1) + 2,
      border: '1px solid #D9D9D9',
      ...bs,
    }"
  >
    <template
      v-for="(prevContext, index) of contexts"
      :key="`context-${prevContext ? prevContext.lookupKey : index}`"
    >
      <Divider
        v-if="index !== 0"
        :vertical="true"
        :bs="{ bg: '#d9d9d9' }"
      />
      <CascadeLevel
        :prev-context="prevContext"
        :level="index + 1"
        :children-state="childrenState"
        :active-id="viewValue[index] ? viewValue[index][identKey] : null"
        :value="modelValue"
        :on-check-item="onCheckItem"
        :has-children="hasChildren"
        :set-children="setChildren"
        :ident-key="identKey"
        :value-key="valueKey"
        :text-key="textKey"
        :on-view="item => onViewItem(index, item)"
        v-bind="levelProps"
      />
    </template>
  </NewBsBox>
</template>

<script>
  import { flatten, uniqBy } from 'lodash'
  import NewBsBox from '@xhs/yam-beer/cmp/primitive/NewBsBox'
  import { Divider } from '@xhs/yam-beer/cmp/common'
  import {
    getContext,
    ROOT_KEY,
    loadChildrenAndSet,
    getLookupkey,
  } from '@xhs/yam-beer/utils/cascadeUtils'
  import CascadeLevel from './Level.vue'

  /**
   * @model modelValue|@update:modelValue
   */
  export default {
    name: 'MultiCascadeTree',
    components: {
      NewBsBox,
      Divider,
      CascadeLevel,
    },
    model: {
      prop: 'modelValue',
      event: 'update:modelValue',
    },
    props: {
      /**
       * 容器 BeerStyle
       */
      bs: {
        type: Object,
        default: () => ({}),
      },
      /**
       * 如果是 idPaths，支持根据 modelValue lazy 预获取
       */
      valueFormat: {
        type: String,
        default: 'idPaths',
      },
      /**
       * 是否 modelValue 包含根元素
       * 在 idPaths 下对初始选中 value 值比较重要的属性
       */
      includeRootValue: {
        type: Boolean,
        default: true,
      },
      /**
       * 预获取树状结构的限制数量，在接口不支持 lazy load 的小数量数据有用
       */
      prefetchLimit: {
        type: Number,
        default: 0,
      },
      /**
       * 判断选项是否带有子节点
       */
      hasChildren: {
        type: Function,
        default: item => item.hasChildren,
      },
      /**
       * 获取对应级别的选项 Promise 接口
       * { level, context } 参数
       * level: 1,2,3,4
       * context: 该 level 下选中 parent 的选项
       */
      getTree: {
        type: Function,
        required: true,
      },
      /**
       * 选项级数
       */
      levelNums: {
        type: Number,
        default: 2,
      },
      /**
       * value 必须是逗号分割 id 列表的字符串
       */
      modelValue: {
        type: Array,
        default: () => ([]),
      },
      /**
       * 透传给 Level 组件的 props
       */
      levelProps: {
        type: Object,
        default: () => ({}),
      },
      /**
       * 解析 value 显示中文名称的字段
       */
      textKey: {
        type: String,
        default: 'name',
      },
      /**
       * 选项 unique id key, 建立索引使用
       */
      identKey: {
        type: String,
        required: true,
      },
      /**
       * 选项值的 key，上传给后端使用
       */
      valueKey: {
        type: String,
        default: 'value',
      },
      /**
       * 提供初始的 id - name 解析关系
       */
      valueIdentity: {
        type: Object,
        default: () => ({}),
      },
    },
    emits: ['update:modelValue'],
    data() {
      return {
        childrenState: {},
        viewValue: [],
        localValue: [],
        localLevelNums: this.levelNums,
      }
    },
    computed: {
      contexts() {
        const contexts = getContext(
          this.localLevelNums,
          this.childrenState,
          this.viewValue,
          this.identKey,
        )
        let isParentChecked = false

        return contexts
          .map((c, i) => {
            if (!c) {
              return c
            }
            const context = {
              parentChecked: isParentChecked,
              ...c,
            }
            const viewValue = this.viewValue[i]
            if (!isParentChecked
              && viewValue
              && this.modelValue.indexOf(viewValue[this.valueKey]) > -1
            ) {
              isParentChecked = true
            }
            return context
          })
      },
      itemNameByIdentity() {
        const values = Object.keys(this.childrenState)
          .reduce((acc, key) => {
            const state = this.childrenState[key]
            state.children.forEach(item => {
              acc[item[this.valueKey]] = item[this.textKey]
            })
            return acc
          }, {})
        return {
          ...this.valueIdentity,
          ...values,
        }
      },
    },
    watch: {
      modelValue: {
        handler(v = []) {
          const vm = this
          this.localValue = [...v]
          if (vm.valueFormat === 'idPaths') {
            const nestedValues = v.map(i => {
              const idPaths = `${i}`.split(',')
              return idPaths.map((id, level) => {
                if (this.includeRootValue && level === 0) {
                  return null
                }
                if (level + 1 >= this.localLevelNums) {
                  return null
                }
                return {
                  level,
                  id,
                  idPaths: idPaths.slice(0, idPaths.length - level),
                }
              })
            })
            uniqBy(
              flatten(nestedValues).filter(i => !!i),
              i => `${i.level}${i.id}`,
            )
              .forEach(i => {
                const { level, id, idPaths } = i
                vm.fetchItem(
                  this.includeRootValue ? Number(level) : Number(level) + 1,
                  {
                    [vm.identKey]: id,
                    [vm.valueKey]: idPaths.join(','),
                  },
                  vm.identKey,
                )
              })
          }
        },
        immediate: true,
      },
    },
    created() {
      this.fetchItem(0, null, true)
    },
    methods: {
      fetchItem(level, item, fetchAll = false) {
        const lookupKey = level === 0
          ? ROOT_KEY
          : getLookupkey(level, item[this.identKey])
        const q = this.loadChildren({
          parent: item,
          level: level + 1,
          lookupKey,
        })
        if (fetchAll) {
          q.then(({ children = [] }) => {
            children.forEach((c, index) => {
              if (index < this.prefetchLimit) {
                this.fetchItem(level + 1, c, fetchAll)
              }
            })
          })
        }
        return q
      },
      /**
       * 需要引入 localValue 是因为 onCheckItem 可能会段时间内多次被调用
       * 直接使用 modelValue 可能会导致部分更新未更新上
       */
      onCheckItem(item, isRemove) {
        const value = item[this.valueKey]
        if (isRemove) {
          this.localValue = this.localValue.filter(i => i !== value)
          /**
           * 勾选后 触发的事件
           * 可以用 v-model 双向绑定
           *
           * @property {array} value - cascade 值
           */
          this.$emit(
            'update:modelValue',
            this.localValue,
            { item, isRemove, itemNameByIdentity: this.itemNameByIdentity },
          )
        } else {
          this.localValue = [
            ...this.localValue,
            value,
          ]
          this.$emit(
            'update:modelValue',
            this.localValue,
            { item, isRemove, itemNameByIdentity: this.itemNameByIdentity },
          )
        }
      },
      onViewItem(index, item) {
        const level = index + 1
        const newV = item ? [item] : []
        if (level === 1) {
          this.viewValue = newV
        } else {
          this.viewValue = this.viewValue.slice(
            0, level - 1,
          ).concat(newV)
        }
        const q = new Promise((resolve, reject) => {
          if (item) {
            if (level >= this.localLevelNums && this.hasChildren(item)) {
              this.localLevelNums += 1
            } else if (this.localLevelNums > this.levelNums && level < this.levelNums) {
              // auto close overflow level when current click level has no child
              this.localLevelNums = this.levelNums
            }
            this.$nextTick(() => {
              this.fetchItem(level, item).then(resolve).catch(reject)
            })
          } else {
            resolve()
          }
        })
        return q
      },
      setChildren(lookupKey, value, newV = {}) {
        this.childrenState = {
          ...this.childrenState,
          [lookupKey]: {
            lookupKey,
            ...value,
          },
          ...newV,
        }
      },
      loadChildren({ level, lookupKey, parent }) {
        return loadChildrenAndSet(level, lookupKey, parent, {
          childrenState: this.childrenState,
          levelNums: this.localLevelNums,
          setChildren: this.setChildren,
          hasChildren: this.hasChildren,
          identKey: this.identKey,
          getTree: this.getTree,
        })
      },
    },
  }
</script>
